import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import supabase from './supabaseClient';
import { apiPost } from '../services/apiClient';
import { validateRecentlyViewedCompanies } from '../utils/searchUtils';

// Immediately extract and clear auth-related hashes BEFORE React renders anything
// This prevents other components (like CitationScroller) from seeing the hash
let initialAuthHash = null;
let initialSsoError = null;
if (typeof window !== 'undefined') {
    // Pick up SSO redirect errors FIRST (before replaceState clears the URL)
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('sso_error')) {
        initialSsoError = searchParams.get('sso_error');
    }

    const hash = window.location.hash;
    if (hash && (hash.includes('access_token') || hash.includes('error='))) {
        initialAuthHash = hash;
    }

    // Clean up URL (remove both hash and query params)
    if (initialAuthHash || initialSsoError) {
        window.history.replaceState(null, '', window.location.pathname);
    }
}

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {

    // Environment detection
    const isDevelopment = process.env.NODE_ENV === 'development';

    // Server addresses from environment variables (set per environment in Vercel)
    // Falls back to localhost in development, production URLs otherwise
    const serverAddress = process.env.REACT_APP_BACKEND_URL
        || (isDevelopment ? 'http://localhost:3001/' : 'https://sb-backend.vercel.app/');
    const flaskServerAddress = process.env.REACT_APP_FLASK_URL
        || (isDevelopment ? 'http://127.0.0.1:5000/' : 'https://hnapi-rosy.vercel.app/');
    const processingServerAddress = process.env.REACT_APP_PROCESSING_URL
        || (isDevelopment ? 'http://127.0.0.1:8000/' : 'https://sb-chrome-api.vercel.app/');

    // Debug: log environment and backend URLs on startup
    console.log(`[Backend] NODE_ENV=${process.env.NODE_ENV}, using ${process.env.REACT_APP_BACKEND_URL ? 'REACT_APP_BACKEND_URL' : 'default'}: ${serverAddress}`);

    const [userDoesNotExist, setUserDoesNotExist] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [userOrg, setUserOrg] = useState('');
    const [ip, setIp] = useState('');
    const mobileWidth = 768;
    const [scuttlesiftStatus, setScuttlesiftStatus] = useState('free');
    const [userIsPremium, setUserIsPremium] = useState(true);

    const [isMobile, setIsMobile] = useState(window.innerWidth < mobileWidth);

    const [session, setSession] = useState(null);
    const [token, setToken] = useState(null);
    const [userId, setUserId] = useState(null);
    const [userDetails, setUserDetails] = useState(null);

    // Auth error state (for expired links, SSO errors, etc.)
    // Map standardized error codes to user-friendly messages (prevents XSS from arbitrary URL params)
    const SSO_ERROR_MESSAGES = {
        invite_only: 'Scuttlebutt Research is currently invite-only. Please contact us for access.',
        domain_mismatch: 'The email provided by your organization does not match the allowed domain.',
        state_mismatch: 'Your login session expired or was invalid. Please try again.',
        invalid_profile: 'Your identity provider did not return a valid email. Please contact your IT administrator.',
        create_failed: 'Failed to create your account. Please try again or contact support.',
        setup_failed: 'Account setup incomplete. Please try logging in again.',
        session_failed: 'Failed to establish a session. Please try again.',
        sso_unavailable: 'SSO is temporarily unavailable. Please try again later.',
        sso_failed: 'SSO authentication failed. Please try again.',
    };
    const ssoErrorMessage = initialSsoError
        ? (SSO_ERROR_MESSAGES[initialSsoError] || 'SSO login failed. Please try again or contact your administrator.')
        : null;
    const [authError, setAuthError] = useState(ssoErrorMessage);

    // Auth success message state (for email confirmation, etc.)
    const [authSuccess, setAuthSuccess] = useState(null);

    // Track if we've initialized to prevent duplicate processing
    const initializedRef = useRef(false);

    // Helper to update all session-related state
    const updateSessionState = (newSession) => {
        if (newSession) {
            setSession(newSession);
            setToken(newSession.access_token);
            setUserId(newSession.user.id);
            setUserDoesNotExist(false);

            // Persist token to localStorage for apiClient to use
            localStorage.setItem('token', newSession.access_token);
            localStorage.setItem('userId', newSession.user.id);

            // Clean up recently viewed companies that user may no longer have access to
            validateRecentlyViewedCompanies(apiPost);

            // Cache user details from localStorage if available
            const cachedUserDetails = localStorage.getItem('userDetails');
            if (cachedUserDetails) {
                setUserDetails(JSON.parse(cachedUserDetails));
            }
        } else {
            setSession(null);
            setToken(null);
            setUserId(null);
            setUserDoesNotExist(true);
            setUserDetails(null);

            // Clear localStorage
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            localStorage.removeItem('userDetails');
        }
    };

    // Handle auth tokens/errors in URL hash (password recovery, email confirmation, errors, etc.)
    useEffect(() => {
        const handleAuthRedirect = async () => {
            if (!initialAuthHash || initialAuthHash.length <= 1) return;

            const params = new URLSearchParams(initialAuthHash.substring(1));

            // Handle Supabase auth errors (expired links, etc.)
            const error = params.get('error');
            if (error) {
                const errorDescription = params.get('error_description');
                setAuthError(errorDescription || error);
                // Clear the hash reference so we don't process it again
                initialAuthHash = null;
                return;
            }

            // Handle successful auth redirects
            const accessToken = params.get('access_token');
            const refreshToken = params.get('refresh_token');
            const type = params.get('type');

            if (!accessToken || !refreshToken) return;

            // Clear the hash reference so we don't process it again
            initialAuthHash = null;

            try {
                // Set the session in Supabase - this will trigger onAuthStateChange
                const { error: sessionError } = await supabase.auth.setSession({
                    access_token: accessToken,
                    refresh_token: refreshToken
                });

                if (sessionError) {
                    console.error('Error setting session from redirect:', sessionError);
                    setAuthError('Failed to process the link. Please try again.');
                    return;
                }

                // Handle specific redirect types
                if (type === 'recovery') {
                    // Password recovery - redirect to update password page
                    window.location.href = '/update-password';
                } else if (type === 'signup' || type === 'email_change' || type === 'magiclink') {
                    // Email confirmed - set flag to show login modal on landing page
                    sessionStorage.setItem('email_confirmed', 'true');
                    window.location.href = '/';
                }
            } catch (err) {
                console.error('Error processing auth redirect:', err);
                setAuthError('Failed to process the authentication link.');
            }
        };

        handleAuthRedirect();
    }, []);

    // Main auth state management - Supabase is the single source of truth
    useEffect(() => {
        if (initializedRef.current) return;
        initializedRef.current = true;

        // Get initial session from Supabase
        const initializeAuth = async () => {
            try {
                const { data: { session: currentSession }, error } = await supabase.auth.getSession();

                if (error) {
                    console.error('Error getting initial session:', error);
                } else {
                    updateSessionState(currentSession);
                }
            } catch (err) {
                console.error('Error initializing auth:', err);
            } finally {
                setIsLoading(false);
            }
        };

        initializeAuth();

        // Listen for auth state changes from Supabase
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
            console.log('Auth state changed:', event);

            switch (event) {
                case 'SIGNED_IN':
                case 'TOKEN_REFRESHED':
                    updateSessionState(newSession);
                    break;

                case 'SIGNED_OUT':
                    updateSessionState(null);
                    break;

                case 'USER_UPDATED':
                    // User profile was updated (e.g., password change)
                    if (newSession) {
                        updateSessionState(newSession);
                    }
                    break;

                case 'PASSWORD_RECOVERY':
                    // User clicked password recovery link - session is set
                    // The URL hash handler will redirect to /update-password
                    updateSessionState(newSession);
                    break;

                default:
                    // Handle any other events
                    console.log('Unhandled auth event:', event);
            }
        });

        return () => {
            subscription?.unsubscribe();
        };
    }, []);

    const fetchUserDetails = async () => {
        try {
            const response = await apiPost('user/details', {});
            if (!response.ok) {
                throw new Error('Failed to fetch user details');
            }
            const data = await response.json();
            localStorage.setItem('userDetails', JSON.stringify(data));
            setUserDetails(data);
            setScuttlesiftStatus(data.scuttlesift_status);
        } catch (error) {
            console.error('Error fetching user details:', error);
        }
    };

    // Fetch user details when session changes
    useEffect(() => {
        if (session && !userDetails) {
            fetchUserDetails();
        }
    }, [session, userDetails]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < mobileWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loginUser = async (email, password) => {
        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password
            });

            if (error) {
                // Handle specific error cases
                if (error.message.includes('Email not confirmed')) {
                    return 'Please confirm your email address before signing in. Check your inbox for the confirmation link.';
                }
                return error.message;
            }

            // Immediately update state and localStorage so API calls work right away
            // (onAuthStateChange is async and might not have fired yet)
            if (data.session) {
                updateSessionState(data.session);
            }

            // Clear any signup success message
            sessionStorage.removeItem('signup_success');

            return null; // No error
        } catch (error) {
            console.error('Login error:', error.message);
            return error.message;
        }
    };

    const logOut = async () => {
        try {
            const { error } = await supabase.auth.signOut();
            if (error) throw error;

            // onAuthStateChange will handle clearing the session state
            // Clear cached user details
            localStorage.removeItem('userDetails');
            setScuttlesiftStatus('free');

            // Redirect to landing page
            window.location.href = '/';
        } catch (error) {
            console.error('Logout error:', error.message);
            return error.message;
        }
    };

    const signUpUser = async (email, password, name, company_name) => {
        try {
            const response = await fetch(serverAddress + 'auth/signup', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    email: email,
                    password: password,
                    full_name: name,
                    org_name: company_name,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Signup successful:', data);
                return { success: true, requiresConfirmation: true };
            } else {
                const errorData = await response.json();
                console.error('Signup failed:', errorData);
                return { success: false, error: errorData.error };
            }
        } catch (error) {
            console.error('Signup error:', error);
            return { success: false, error: 'Network error. Please try again.' };
        }
    };

    const initiateSSO = async (tenant) => {
        try {
            const response = await fetch(`${serverAddress}auth/sso/authorize`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ tenant }),
            });
            const data = await response.json();
            if (!response.ok || !data.redirect_url) {
                setAuthError(data.error || 'SSO login failed. Please try again.');
                return;
            }
            window.location.href = data.redirect_url;
        } catch (err) {
            setAuthError('Failed to initiate SSO. Please try again.');
        }
    };

    const clearAuthError = () => setAuthError(null);
    const clearAuthSuccess = () => setAuthSuccess(null);

    return (
        <AuthContext.Provider value={{
            session, setSession,
            userId,
            token,
            userDoesNotExist, isLoading, userOrg, ip, isMobile, scuttlesiftStatus, setScuttlesiftStatus,
            userIsPremium, userDetails, fetchUserDetails,
            logOut, loginUser, signUpUser, initiateSSO,
            serverAddress, flaskServerAddress, processingServerAddress,
            authError, clearAuthError,
            authSuccess, clearAuthSuccess
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
